'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from './AuthContext';
import { save1RMToFirebase } from './firebaseSync';
import { getCanonical1RMKey, consolidateOneRMKeys } from './oneRM';
import { needsMigration, migrateFromLocalStorage } from './db';
import { startPeriodicSync, syncAll } from './dexieFirebaseSync';
import { storage } from './storage';
// Subscription is now handled by RevenueCat
// Use useProAccess() hook instead of AppContext for subscription checks

interface AppState {
  currentWeek: number;
  currentDay: number;
  oneRMs: Record<string, number>;
  dexieReady: boolean;
  /**
   * True once week/day have been read from the authoritative store, rather
   * than still sitting on the `useState(1)` defaults.
   *
   * `currentWeek` starts at 1 and is overwritten asynchronously. Anything that
   * takes an IRREVERSIBLE action off week/day — navigation, session creation —
   * must wait for this, or it acts on "week 1" for every user. That is exactly
   * how a week-4 athlete got redirected into the week-1 assessment: week 1 is
   * an assessment week, so the check passed against the default before Dexie
   * hydration landed. Rendering may use the defaults; deciding may not.
   */
  programStateLoaded: boolean;
  setCurrentWeek: (week: number) => void;
  setCurrentDay: (day: number) => void;
  updateOneRM: (exercise: string, value: number) => void;
}

const AppContext = createContext<AppState | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const router = useRouter();
  const [currentWeek, setCurrentWeek] = useState(1);
  const [currentDay, setCurrentDay] = useState(1);
  const [oneRMs, setOneRMs] = useState<Record<string, number>>({});
  const [dexieReady, setDexieReady] = useState(false);
  const [programStateLoaded, setProgramStateLoaded] = useState(false);

  // Push notification deep-link router. Wired here (not in pushNotifications.ts)
  // because we need Next.js's client-side router — window.location.href doesn't
  // work against `output: 'export'` paths in the Capacitor WebView.
  useEffect(() => {
    const onDeepLink = (e: Event) => {
      const deepLink = (e as CustomEvent<string>).detail;
      if (typeof deepLink === 'string' && deepLink.length > 0) {
        console.log('[push] Navigating to deep link:', deepLink);
        router.push(deepLink);
      }
    };
    window.addEventListener('pushDeepLink', onDeepLink);
    return () => window.removeEventListener('pushDeepLink', onDeepLink);
  }, [router]);

  // Initialize Dexie and migrate from localStorage if needed
  useEffect(() => {
    async function initDexie() {
      if (!user?.uid) return;
      
      try {
        const shouldMigrate = await needsMigration();
        
        if (shouldMigrate) {
          console.log('� Migrating data to Dexie...');
          const stats = await migrateFromLocalStorage(user.uid);
          console.log(' Dexie migration complete:', stats);
        }
        
        setDexieReady(true);
      } catch (error) {
        console.error(' Dexie initialization failed:', error);
        // Continue with localStorage fallback
        setDexieReady(true);
      }
    }
    
    initDexie();
  }, [user?.uid]);

  // Start periodic background sync when Dexie is ready
  useEffect(() => {
    if (!user?.uid || !dexieReady) return;

    // Do initial sync
    syncAll(user.uid).catch(err => {
      console.error(' Initial sync failed:', err);
    });

    // Initialize local + push notification systems
    import('@/lib/notifications').then(({ initializeNotifications, scheduleAllReminders }) => {
      initializeNotifications().catch(err => console.warn('Local notification init failed:', err));
      scheduleAllReminders(user.uid).catch(err => console.warn('Reminder scheduling failed:', err));
    }).catch(() => {});
    import('@/lib/pushNotifications').then(({ initializePushNotifications }) => {
      initializePushNotifications(user.uid).catch(err => console.warn('Push notification init failed:', err));
    }).catch(() => {});

    // Start periodic sync (every 5 minutes)
    const stopSync = startPeriodicSync(user.uid);

    // Sync when app/tab becomes visible (PWA foregrounded, tab switched to)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        console.log(' App visible — syncing...');
        syncAll(user.uid).catch(err => {
          console.error(' Visibility sync failed:', err);
        });
      }
    };

    // Sync when device comes back online
    const handleOnline = () => {
      console.log(' Back online — syncing...');
      syncAll(user.uid).catch(err => {
        console.error(' Online sync failed:', err);
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    // Cleanup on unmount
    return () => {
      stopSync();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [user?.uid, dexieReady]);

  // Load 1RMs from localStorage on mount (week/day now comes from Dexie)
  useEffect(() => {
    const saved = storage.get('tacticalFitnessState');
    const legacyOneRMs = storage.get('oneRMs');

    // Restore week/day from localStorage as a fast initial value.
    // Dexie sync (below) is authoritative and will overwrite these.
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setCurrentWeek(data.currentWeek || 1);
        setCurrentDay(data.currentDay || 1);
      } catch { /* corrupted localStorage — Dexie sync will fix */ }
    }

    // Load 1RMs — consolidate case/format-variant keys on the way in (e.g.
    // "backSquat" vs "backsquat") so the in-memory store, and everything it
    // writes downstream (localStorage + Firestore), stays canonical. This is
    // half the fix for the legacy dup-key corruption; updateOneRM is the other.
    const hydrateOneRMs = (raw: Record<string, number>) => {
      try { setOneRMs(consolidateOneRMKeys(raw).cleaned); }
      catch { setOneRMs(raw); }
    };
    if (saved) {
      const data = JSON.parse(saved);
      if (data.oneRMs && Object.keys(data.oneRMs).length > 0) {
        hydrateOneRMs(data.oneRMs);
      } else if (legacyOneRMs) {
        hydrateOneRMs(JSON.parse(legacyOneRMs));
      }
    } else if (legacyOneRMs) {
      hydrateOneRMs(JSON.parse(legacyOneRMs));
    }
  }, []);

  // Sync program state from Dexie/Firestore (authoritative source for week/day)
  useEffect(() => {
    if (!user?.uid || !dexieReady) return;

    (async () => {
      try {
        const { getUserProgramState } = await import('@/lib/dayTracking');
        const state = await getUserProgramState(user.uid);
        if (state) {
          setCurrentWeek(state.currentWeek);
          setCurrentDay(state.currentDay);
        }
      } catch (err) {
        console.error('AppContext: Failed to sync program state from Dexie:', err);
      } finally {
        // Set even on failure/no-state: consumers need to know the read has
        // been ATTEMPTED, otherwise a user with no saved state would block
        // week/day-dependent behaviour forever.
        setProgramStateLoaded(true);
      }
    })();
  }, [user?.uid, dexieReady]);

  // Save to localStorage whenever state changes
  useEffect(() => {
    storage.set('tacticalFitnessState', JSON.stringify({
      currentWeek,
      currentDay,
      oneRMs,
    }));
    // Maintain legacy key for compatibility
    storage.set('oneRMs', JSON.stringify(oneRMs));
  }, [currentWeek, currentDay, oneRMs]);

  // Sync 1RMs to Firebase when they change
  useEffect(() => {
    if (user && Object.keys(oneRMs).length > 0) {
      // Debounce Firebase sync
      const timeoutId = setTimeout(() => {
        save1RMToFirebase(user.uid, oneRMs).catch(err => {
          console.warn(' Firebase 1RM sync failed:', err);
        });
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [oneRMs, user]);

  // Subscription is now handled by RevenueCat
  // Use useProAccess() hook in components that need subscription checks

  const updateOneRM = (exercise: string, value: number) => {
    // Write to the CANONICAL key and drop any existing case/format variants for
    // the same lift, so an explicit edit wins cleanly and we never grow a second
    // variant key. This is the source-side fix for the legacy 1RM dup-key
    // corruption (save1RMToFirebase merge-wrote whatever variants the state held).
    // Detect variants with the SAME normalization consolidateOneRMKeys uses for
    // grouping (lowercase + strip non-alphanumerics) — getCanonical1RMKey alone
    // isn't idempotent for lifts outside the alias map.
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const canonical = getCanonical1RMKey(exercise);
    // Drop existing keys matching the input's norm OR the canonical key's norm,
    // so neither a case/spacing variant nor a stale key colliding with the
    // canonical survives alongside the value we're about to write.
    const drop = new Set([norm(exercise), norm(canonical)]);
    setOneRMs(prev => {
      const next: Record<string, number> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (!drop.has(norm(k))) next[k] = v;
      }
      next[canonical] = value;
      return next;
    });
  };

  return (
    <AppContext.Provider value={{
      currentWeek,
      currentDay,
      oneRMs,
      dexieReady,
      programStateLoaded,
      setCurrentWeek,
      setCurrentDay,
      updateOneRM,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}

